/**
 * English interface strings.
 *
 * Typed as `Dictionary`, so a missing or misnamed key is a compile error.
 * German legal citations are kept verbatim — § 49 Abs. 4 PBefG is the name of
 * the provision, and a translated citation cannot be looked up. Where the
 * surrounding sentence explains one, the explanation is translated and the
 * citation left alone.
 */

import type { Dictionary } from './de';

export const en: Dictionary = {
  common: {
    loading: 'Loading …',
    save: 'Save',
    saving: 'Saving …',
    cancel: 'Cancel',
    back: 'Back',
    remove: 'Remove',
    from: 'From',
    to: 'To',
    status: 'Status',
    search: 'Search',
    all: 'All',
    dash: '—',
    saveFailed: 'Saving failed.',
    unknown: 'Unknown',
  },

  nav: {
    overview: 'Overview',
    trips: 'Trips',
    orders: 'Orders',
    shifts: 'Shifts',
    invoices: 'Invoices',
    export: 'Export',
    backup: 'Backup',
    settings: 'Settings',
    more: 'More',
    lock: 'Lock',
    closeMenu: 'Close menu',
  },

  shell: {
    backupDueTitle: 'Backup due',
    backupNow: 'Back up now',
    companyMissingTitle: 'Company details missing',
    companyMissingBody:
      'Without a company name, address and tax number no invoice can be issued under § 14 Abs. 4 UStG.',
    companyMissingLink: 'Enter them now',
    vehicleMissingTitle: 'No vehicle yet',
    vehicleMissingBody: 'Trips and shifts need a vehicle with a number plate.',
    vehicleMissingLink: 'Add a vehicle',
    autoLocked: (count: number) =>
      count === 1 ? '1 trip was locked automatically.' : `${count} trips were locked automatically.`,
    autoLockedNote: 'Corrections are still possible but are recorded in the audit log.',
    footerPrivacy:
      'All data is encrypted on this device. Nothing is sent to a server. Addresses go to Geoapify (EU) only when automatic distance calculation is switched on.',
    footerDisclaimer:
      'Not tax advice. Electronic logbooks are not certified in Germany; whether one is accepted is decided by the auditor case by case.',
    decrypting: 'Decrypting data …',
  },

  welcome: {
    title: 'Fahrtenbuch Pro',
    subtitle: 'Sign in or start a new logbook',
    separateNote:
      'A new account keeps its own separate logbook. Trips, invoices and company details are not shared.',
    noAccountYet: 'There is no account on this device yet. Create one first.',
    back: 'Back',
  },

  setup: {
    stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
    languageTitle: 'Choose a language',
    languageSubtitle: 'You can change this later in the settings.',
    languageNote:
      'Invoices, the GoBD archive and the Verfahrensdokumentation always stay German — they go to customers and to the tax office.',
    continue: 'Continue',
    accountTitle: 'Set up your logbook',
    accountSubtitle: 'Your data stays on this device. There is no server.',
    name: 'Name',
    password: 'Password',
    passwordRepeat: 'Repeat password',
    passwordHint: 'At least 12 characters. It encrypts all data on this device.',
    passwordStrength: (label: string, issue?: string) =>
      `Strength: ${label}${issue ? ` — ${issue}` : ''}`,
    mismatch: 'Does not match.',
    nameRequired: 'Please enter a name.',
    passwordsDiffer: 'The passwords do not match.',
    passwordTooShort: 'The password must be at least 12 characters long.',
    deriving: 'Deriving key …',
    createAccount: 'Create account',
    separateBooks:
      'Each account keeps its own separate logbook. Trips, invoices and company details are not shared.',
    argonNote:
      'Deriving the key deliberately takes a moment (Argon2id, 64 MiB). That is what protects the data if the device is lost.',
    createFailed: 'The account could not be created.',

    recoveryTitle: 'Recovery code',
    recoverySubtitle: 'Shown once — please write it down now.',
    recoveryWarningTitle: 'Without this code your data is lost',
    recoveryWarningBody:
      'There is no server that could reset your password. If you lose both the password and this code, access to every trip and invoice is gone for good.',
    copy: 'Copy',
    saveAsFile: 'Save as file',
    acknowledge:
      'I have written the code down and stored it somewhere safe away from this device.',
    usernameNote: (username: string) => `Your username for signing in: ${username}`,

    companyTitle: 'Company details',
    companySubtitle: 'These appear on every invoice. You can skip this step.',
    companyNote:
      'Without them you can record trips but cannot issue an invoice — § 14 Abs. 4 UStG requires them.',
    vehicleTitle: 'First vehicle',
    vehicleSubtitle: 'Trips and shifts are assigned to a vehicle.',
    vehicleNote:
      'The vehicle number is the “Mietwagenkennung” from the BMF circular of 11 March 2024 and appears in every export.',
    skip: 'Skip',
    skipNote: 'Can be added later under Settings.',
    finish: 'Done — open the app',
  },

  login: {
    title: 'Sign in',
    subtitle: 'Fahrtenbuch Pro',
    greeting: (name: string) => `Welcome back, ${name}.`,
    driver: 'Driver',
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    decrypting: 'Decrypting …',
    failed: 'Sign-in failed.',
    forgot: 'Forgotten your password? Reset it with the recovery code',
    register: 'Create a new account',

    recoverTitle: 'Reset password',
    recoverSubtitle: 'Using the recovery code',
    code: 'Recovery code',
    codeHint: 'Case and hyphens do not matter.',
    newPassword: 'New password',
    newPasswordRepeat: 'Repeat new password',
    minChars: 'At least 12 characters.',
    strength: (label: string) => `Strength: ${label}`,
    reset: 'Reset password',
    checking: 'Checking …',
    backToLogin: 'Back to sign in',
    unknownUser: 'Unknown username.',
    resetFailed: 'Reset failed.',
    newCodeTitle: 'New recovery code',
    newCodeSubtitle: 'The old code no longer works.',
    notedContinue: 'Noted — open the app',
    continueToApp: 'Continue to the app',
  },

  dashboard: {
    greeting: (firstName: string) => `Hello, ${firstName}`,
    tripsThisMonth: 'Trips this month',
    kilometers: 'Kilometres',
    revenueGross: 'Revenue, gross',
    outstanding: 'Outstanding',
    uninvoiced: (count: number) =>
      count === 1 ? '1 trip not yet invoiced' : `${count} trips not yet invoiced`,
    createInvoice: 'Create invoice',
    uninvoicedBody: (amount: string) => `${amount} from this month is still open.`,
    recent: 'Recently recorded',
    showAll: 'Show all',
    noTrips: 'No trips this month yet',
    firstTrip: 'Record the first trip',
    systemStatus: 'System status',
    lastBackup: 'Last backup',
    backupLink: 'back up',
    distanceAuthority: 'Authoritative for distance',
    distanceComputed: 'calculated distance',
    distanceOdometer: 'odometer',
    vat: 'VAT',
    vatKleinunternehmer: 'Small business (§ 19 UStG)',
    vatTaxi: 'Taxi — 7 % up to 50 km',
    vatMietwagen: 'Private hire — 19 %',
    newTrip: 'Record a trip',
  },

  trips: {
    title: 'Trips',
    description: 'Every trip recorded in the selected period.',
    new: 'Record a trip',
    filterOpen: 'Not yet invoiced',
    filterInvoiced: 'Invoiced',
    searchPlaceholder: 'Name or address',
    totalTrips: 'trips',
    totalGross: 'gross',
    empty: 'No trips in this period',
    firstTrip: 'Record the first trip',
    badgeLocked: 'locked',
    badgeDistanceFixed: 'distance corrected',
    badgeInvoiced: 'invoiced',
    badgeOpen: 'open',

    editTitle: 'Edit trip',
    lockedNotice: (date: string) =>
      `Locked on ${date}. Changes are recorded and disclosed in every export.`,
    sectionRoute: 'Passenger and route',
    passenger: 'Passenger',
    passengerHint: 'A surname is enough — the rest is filled in.',
    fromLabel: 'From',
    toLabel: 'To',
    distance: 'Distance (km)',
    distanceOverridden: 'Changed by hand — this is recorded.',
    compute: 'Calculate',
    computing: 'Calculating …',
    noGeoKey:
      'Automatic distance calculation is not set up. Add a Geoapify key under Settings, or keep entering the distance by hand.',
    sectionTimes: 'Times and vehicle',
    date: 'Date',
    vehicle: 'Vehicle',
    choose: '— please choose —',
    startTime: 'Start of trip',
    endTime: 'End of trip',
    sectionBilling: 'Billing',
    fareGross: 'Fare, gross (€)',
    tip: 'Tip (€)',
    tipHint: 'Only enter this where it matters for tax.',
    paymentMethod: 'Payment method',
    tripType: 'Trip type',
    tripTypeHint: 'Mandatory field under BMF 11.03.2024.',
    vatLine: (rate: number, basis: string) => `VAT ${rate} % · ${basis}`,
    vatOf: (amount: string) => ` · of which ${amount} VAT`,
    sectionOrder: 'Order and notes',
    linkedOrder: 'Related order',
    linkedOrderHint: 'Links the trip to the record required by § 49 Abs. 4 Satz 4 PBefG.',
    noOrder: '— no order linked —',
    comment: 'Comment',
    commentPlaceholder: 'e.g. waiting time, special requests, intermediate stop',
    amendmentReason: 'Reason for the correction',
    amendmentHint: 'Appears in the audit chain and in every export.',
    amendmentPlaceholder: 'e.g. digits transposed in the distance',
    saveAndNext: 'Save and next trip',
    errPassenger: 'Please enter the passenger.',
    errFrom: 'Please enter the pick-up address.',
    errTo: 'Please enter the destination address.',
    errVehicle: 'Please choose a vehicle. Vehicles are added under Settings.',
    errDistance: 'Please enter a distance.',
    errAmendment: 'This trip is locked. Please give a reason for the correction.',
    errNoGeoKey: 'No Geoapify key stored — please enter the distance.',
    errNotLocated: 'The addresses could not be located. Please enter the distance.',
    computed: (km: string, minutes: number) =>
      `Calculated: ${km} km, about ${minutes} minutes driving time.`,
    computeFailed: 'The distance could not be calculated.',
    addManually: 'Please enter the distance by hand.',
    habitual: 'usual',
    tripCount: (count: number) => `${count} trips`,
    notFound: 'Trip not found.',

    detailTitle: (date: string) => `Trip on ${date}`,
    edit: 'Edit',
    lockedOn: (date: string) => `locked on ${date}`,
    distanceFixed: 'Distance corrected by hand',
    sectionTrip: 'Trip',
    times: 'Times',
    route: 'Distance',
    computedWas: (km: string) => `(calculated: ${km} km)`,
    net: 'Net',
    vatOfWhich: 'of which VAT',
    orderNumber: 'Order no.',
    orderReceived: 'Order received',
    orderChannel: 'Channel',
    orderAccepted: 'Order accepted',
    orderNotAccepted: 'not recorded',
    changeLog: 'Change history',
    changeLogNote:
      'The complete, immutable history of this record. Every entry is chained to its predecessor with SHA-256; the chain is verified and included with every export.',
  },

  orders: {
    title: 'Order intake',
    description: 'The record required by § 49 Abs. 4 Satz 4 PBefG.',
    new: 'Record an order',
    intro:
      'Every incoming transport order must be recorded and kept for at least a year; for tax purposes it is six. Record the order when the call comes in — not after the trip.',
    gapTitle: 'Gap in the sequence',
    gapBody: (numbers: string) =>
      `Sequence numbers ${numbers} are missing from the current year. A gap in the order book reads as a suppressed order.`,
    empty: 'No orders recorded yet',
    number: (n: number) => `No. ${n}`,
    noName: 'No name',
    received: (when: string) => `Received ${when}`,
    acceptedAs: (short: string, label: string) => ` · accepted ${short} = ${label}`,
    requested: (when: string) => ` · requested ${when}`,
    linkedTrip: 'Trip linked',
    logTrip: 'Record trip',
    formTitle: (next: number | null) =>
      next === null ? 'New order' : `New order — sequence no. ${next}`,
    groupIntake: 'Intake',
    receivedAt: 'Received at',
    receivedAtHint: 'When the booking came in, not when the trip runs.',
    channel: 'Channel',
    groupOrderer: 'Orderer',
    ordererName: 'Name',
    ordererAddress: 'Address',
    ordererPhone: 'Phone',
    ordererEmail: 'Email',
    groupRide: 'Trip',
    pickup: 'Pick-up point',
    pickupHint: 'Street and house number.',
    destination: 'Destination',
    destinationHint: 'Town and street, as far as known when booking.',
    requestedPickup: 'Requested pick-up',
    groupAcceptance: 'Order accepted',
    acceptedToggle: 'The order has already been accepted',
    acceptancePlace: 'Place of acceptance',
    acceptancePlaceHint: '§ 49 Abs. 4 PBefG allows only these three cases.',
    acceptedAt: 'Accepted at',
    vehicle: 'Vehicle',
    vehicleNone: '— not yet assigned —',
    driver: 'Driver',
    acceptanceLater:
      'Acceptance can be added later. Without it there is no evidence that the order was taken at the business premises, at the operator’s home, or by phone during a trip.',
    notes: 'Note',
    pickupRequired: 'The pick-up point is mandatory.',
    findings: {
      pickupLocation: 'Pick-up point missing — mandatory in the order book.',
      acceptancePlace:
        'Acceptance of the transport order is not recorded. § 49 Abs. 4 PBefG allows only the business premises, the operator’s home, or acceptance by phone during a trip.',
      acceptedAt: 'Time of acceptance missing — mandatory in the order book.',
      vehicleId: 'No vehicle noted for the acceptance — mandatory in the order book.',
      ordererName: 'Orderer not named.',
    },
  },

  shifts: {
    title: 'Shifts',
    description: 'Odometer, takings, and the check against the recorded trips.',
    startToday: 'Start a shift for today',
    empty: 'No shifts in this period',
    emptyBody: 'A shift is created automatically as soon as the first trip of a day is recorded.',
    noVehicle: 'No vehicle',
    tripCount: (count: number) => (count === 1 ? '1 trip' : `${count} trips`),
    closed: 'closed',
    open: 'open',
    odometerMissing:
      'The odometer reading at the start of the shift is missing. It is mandatory under BMF 11.03.2024 Abschnitt III.',
    workStart: 'Start of work',
    workEnd: 'End of work',
    vehicle: 'Vehicle',
    odometerStart: 'Odometer at start (km)',
    odometerEnd: 'Odometer at end (km)',
    breaks: 'Breaks',
    addBreak: 'Add a break',
    noBreaks: 'No break recorded.',
    breakFrom: (n: number) => `Break ${n} from`,
    breakTo: (n: number) => `Break ${n} to`,
    until: 'to',
    remove: 'remove',
    reconciliation: 'Reconciliation',
    sumTrips: 'Sum of trips',
    perOdometer: 'Per odometer',
    difference: 'Difference',
    revenue: 'Takings',
    workingTime: 'Working time, net',
    tripsLonger:
      'The recorded trips are longer than the odometer difference. Please check the distances — this is where an audit starts.',
    odometerLonger:
      'The odometer difference exceeds the recorded trips. That is normal for empty runs and the drive home, but it should be explainable.',
    byPaymentMethod: 'By payment method:',
    tipTotal: (amount: string) => ` · tips ${amount}`,
    confirmedBy: (name: string, date: string) =>
      `Confirmed by ${name} on ${date}. This is the equivalent of the driver’s signature on the paper record.`,
    close: 'Close and confirm the shift',
    errOdometerEnd: 'Please enter the odometer reading at the end of the shift first.',
    closeReason: (name: string) => `Schichtabschluss durch ${name}`,
  },

  invoices: {
    title: 'Invoices',
    outstanding: (amount: string) => `${amount} outstanding`,
    create: 'Create invoice',
    gapTitle: 'Gap in the invoice number range',
    gapEntry: (circle: string, missing: string) => `${circle}: ${missing} missing`,
    gapNote:
      '. In an audit a gap is treated as a suppressed invoice (§ 14 Abs. 4 Nr. 4 UStG). Please resolve it.',
    empty: 'No invoices',
    firstInvoice: 'Create the first invoice',
    draft: 'Draft',
    noCustomer: 'No customer',
    positions: (count: number) => (count === 1 ? '1 line' : `${count} lines`),
    overdueBadge: 'overdue',
    dunningBadge: (level: number) => `Reminder level ${level}`,
    eInvoice: 'E-invoice',
    dueOn: (date: string) => `due ${date}`,
    kleinbetrag: 'Small amount',

    formTitleNew: 'Create invoice',
    formTitleEdit: 'Edit invoice draft',
    formDescription: 'An invoice number is only assigned when the invoice is issued.',
    period: 'Period',
    uninvoicedTrips: (count: number) => `Trips not yet invoiced (${count})`,
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
    noOpenTrips: 'No open trips in the selected period.',
    selectTrips: 'Select trips',
    takeTrips: (count: number) => (count === 1 ? 'Use 1 trip' : `Use ${count} trips`),
    errSelectTrip: 'Please select at least one trip.',
    recipient: 'Recipient',
    customerName: 'Name',
    customerAddress: 'Address',
    customerAddressHint: 'Street, postcode and town — one per line.',
    customerVatId: 'Customer VAT ID',
    positionsTitle: 'Lines',
    netPerUnit: (rate: number) => `net · ${rate} % VAT`,
    netTotal: 'Net',
    vatTotal: 'VAT',
    grandTotal: 'Total',
    kleinbetragBadge: 'Small-amount invoice § 33 UStDV — short form',
    kleinunternehmerBadge: '§ 19 UStG — no VAT',
    mietwagenBadge: 'Private hire § 49 PBefG — 19 %',
    datesAndText: 'Dates and text',
    issueDate: 'Invoice date',
    dueDate: 'Due on',
    serviceFrom: 'Service from',
    serviceTo: 'Service to',
    paymentTerms: 'Payment terms',
    notes: 'Note',
    eInvoiceField: 'E-invoice',
    eInvoiceNone: 'PDF only',
    eInvoiceFacturx: 'Factur-X / ZUGFeRD (PDF with embedded XML)',
    problemsTitle: 'Mandatory details are missing',
    issue: 'Issue invoice',
    issuing: 'Issuing …',
    saveDraft: 'Save as draft',
    issueFailed: 'Issuing failed.',

    detailDraftTitle: 'Invoice draft',
    notFound: 'Invoice not found',
    backToList: 'Back to the list',
    kleinbetragFull: 'Small-amount invoice § 33 UStDV',
    stornoBadge: 'Credit note',
    overdueTitle: 'Payment term exceeded',
    overdueBody: (date: string) => `It was due on ${date}.`,
    lastDunned: (when: string) => `Last reminder sent ${when}.`,
    neverDunned: 'No reminder has been sent yet.',
    position: (n: number) => `Line ${n}`,
    withoutVat: 'no VAT',
    withVat: (rate: number) => `${rate} % VAT`,
    netAmount: 'Net amount',
    vatOn: (rate: number, base: string) => `${rate} % VAT on ${base}`,
    invoiceTotal: 'Invoice total',
    details: 'Details',
    invoiceNumber: 'Invoice number',
    notAssigned: '(not yet assigned)',
    numberCircle: 'Number range',
    servicePeriod: 'Service period',
    recipientTerm: 'Recipient',
    addressTerm: 'Address',
    customerVatTerm: 'Customer VAT ID',
    paidTerm: 'Paid',
    documents: 'Documents',
    invoiceAsPdf: 'Invoice as PDF',
    generating: 'Generating …',
    dunningAsPdf: (label: string) => `${label} as PDF`,
    eInvoiceNote:
      'You already have to be able to receive e-invoices today. Issuing them becomes mandatory for domestic B2B sales on 01.01.2028 — on 01.01.2027 above €800,000 of prior-year turnover. Small businesses are permanently exempt under § 34a UStDV.',
    eInvoiceNotReady: 'Not ready for an e-invoice yet',
    eInvoiceGenerated: (format: string) => `${format} was generated and downloaded.`,
    eInvoiceReason: (format: string) => `E-Rechnung erzeugt: ${format}`,
    eInvoiceLast: (when: string) => `Last generated ${when}.`,
    draftTitle: 'Draft',
    draftBody:
      'This draft has no invoice number yet. One is assigned only when the invoice is issued, so that a discarded draft leaves no gap in the number range.',
    editDraft: 'Edit draft',
    paymentAndDunning: 'Payment and reminders',
    paidOn: (when: string) => `Recorded as paid on ${when}.`,
    paidNoDate: 'Recorded as paid.',
    undo: 'Undo',
    paymentReceived: 'Payment received',
    markPaid: 'Record as paid',
    recording: 'Recording …',
    creating: 'Creating …',
    createDunning: (label: string) => `Create ${label}`,
    cancelReason: 'Reason for cancellation',
    cancelReasonHint: 'The reason appears on the credit note and in the audit log.',
    createStorno: 'Create credit note',
    cancelling: 'Cancelling …',
    cancelNote:
      'An issued invoice is never edited. If it is wrong, the cancellation creates its own document with its own number; the original number stays used so the range remains gapless.',
    cancelInvoice: 'Cancel invoice',
    errCancelReason: 'Please give a reason for the cancellation.',
    errAmount: 'The amount could not be read.',
    errGeneric: 'The operation failed.',
    history: 'Change history',
    problems: {
      companyName: 'The supplier’s full name is missing.',
      companyAddress: 'The supplier’s full address is missing.',
      taxNumber: 'Tax number or VAT identification number is missing.',
      noLines: 'The invoice has no line items.',
      lineDescription: 'One line item has no description of the service.',
      serviceDate: 'The date of supply is missing.',
      customerName: 'The customer’s full name is missing.',
      customerAddress: 'The customer’s full address is missing.',
    },
  },

  exports: {
    title: 'Export',
    description: 'Choose a period and a format. The change history is part of every export.',
    period: 'Period',
    selection: 'Selection',
    month: 'Month',
    year: 'Year',
    custom: 'Custom period',
    check: 'Check the period',
    checking: 'Checking …',
    previewTitle: (period: string) => `Preview — ${period}`,
    trips: 'Trips',
    kilometers: 'Kilometres',
    gross: 'Gross',
    vat: 'VAT',
    chainOk: (count: number) => `Audit chain verified — ${count} entries`,
    chainBad: (count: number) => `Audit chain rejected — ${count} findings`,
    odometerDeviation: 'Odometer deviation in at least one shift',
    odometerMissing: 'Odometer reading missing in at least one shift',
    chainBrokenTitle: 'The audit chain is not intact',
    chainBrokenBody:
      'Export anyway — the verification report is part of the archive — and investigate the finding. A broken chain means the database was changed outside the app.',
    pdfDescription:
      'The human-readable version — with the change history next to each trip and the odometer reconciliation.',
    pdfNote: 'For handing to the tax office as an annex.',
    pdfAction: 'Create PDF',
    xlsxTitle: 'Excel',
    xlsxDescription: 'Trips, shifts, orders, invoices and the audit log as a workbook.',
    xlsxNote: 'For your own analysis and for the tax adviser.',
    xlsxAction: 'Create Excel',
    gobdTitle: 'GoBD archive (Z3)',
    gobdDescription:
      'Structured CSV files with index.xml, verification report and the complete audit chain.',
    gobdNote: 'This is the export for a tax audit.',
    gobdAction: 'Create archive',
    creating: 'Creating …',
    whyThreeTitle: 'Why three formats',
    whyThreeBody:
      'GoBD Rz. 129 names converting original records into a PDF as an example of impermissibly impairing machine readability; Rz. 144 rules print files out as an archive format. Data access under § 147 Abs. 6 AO therefore has to be served with the GoBD archive. PDF and Excel are for people.',
    verfahrenTitle: 'Verfahrensdokumentation',
    verfahrenBody:
      'GoBD Rz. 151–154 requires process documentation setting out the content, structure, workflow and results of the system completely and comprehensibly. Without it an auditor can reject the records on formal grounds. The template is pre-filled from your settings and has to be extended with your own operating procedures.',
    verfahrenDownload: 'Download the template',
    exportFailed: 'Export failed.',
    previewFailed: 'Preview failed.',
  },

  backup: {
    title: 'Backup',
    description: 'Your records exist on this device only.',
    whyTitle: 'Why this is enforced',
    why1:
      'Trip records must be kept for ten years (§ 147 Abs. 1 Nr. 1 in conjunction with Abs. 3 AO). GoBD Rz. 103–104 makes protecting them against loss the taxpayer’s own duty: if the data can no longer be produced, the bookkeeping is formally deficient — which opens the door to an estimate under § 162 AO.',
    why2: (warnDays: number, blockDays: number) =>
      `There is a technical quirk on top of that: iOS can clear a web app’s storage under memory pressure, even when it sits on the home screen. So the app reminds you after ${warnDays} days and blocks the month-end close after ${blockDays} days.`,
    why3:
      'Keep the backup in Germany or the EU. § 146 Abs. 2 AO requires books and records to be kept within the jurisdiction; Abs. 2a permits the EU without approval, while a third country would need authorisation.',
    storageTitle: 'Storage on this device',
    lastBackup: 'Last backup',
    usedStorage: 'Storage used',
    usedOf: (used: string, quota: string, percent: string) => `${used} of ${quota} (${percent} %)`,
    notAvailable: 'not available',
    protected: 'Protected from eviction',
    unknown: 'unknown',
    yes: 'yes',
    no: 'no',
    requestPersistent: 'Request persistent storage',
    createTitle: 'Create a backup',
    createBody:
      'You get two files: an encrypted backup file for restoring, and the complete GoBD archive as a readable CSV package. The passphrase is separate from your sign-in password — write it down, it cannot be reset.',
    passphrase: 'Passphrase',
    passphraseHint: 'At least 12 characters.',
    passphraseRepeat: 'Repeat passphrase',
    label: 'Label',
    labelHint: 'Appears in the file header and helps tell backups apart.',
    labelPlaceholder: 'e.g. monthly backup August',
    create: 'Create backup',
    creating: 'Creating …',
    errShort: 'The passphrase must be at least 12 characters long.',
    errMismatch: 'The passphrases do not match.',
    failed: 'Backup failed.',
    done: (trips: number, invoices: number, entries: number) =>
      `Backup created: ${trips} trips, ${invoices} invoices, ${entries} audit entries. The complete GoBD archive was downloaded as well.`,

    restoreTitle: 'Restore a backup',
    restoreWarning:
      'Restoring replaces all data on this device. Merging is not possible, because the audit chain would then match neither the backup nor the device and could no longer be verified.',
    file: 'Backup file',
    createdAt: 'Created',
    contents: 'Contents',
    contentsValue: (trips: number, shifts: number, invoices: number, entries: number) =>
      `${trips} trips · ${shifts} shifts · ${invoices} invoices · ${entries} audit entries`,
    chainAtBackup: 'Audit chain at backup time',
    chainOk: 'intact',
    chainBad: 'rejected',
    filePassphrase: 'Passphrase of the backup',
    confirmation: 'Confirmation',
    confirmationHint: 'Type WIEDERHERSTELLEN to continue.',
    confirmationWord: 'WIEDERHERSTELLEN',
    errConfirmation: 'Please type WIEDERHERSTELLEN to confirm.',
    replaceAll: 'Replace all data',
    restoring: 'Restoring …',
    restoreFailed: 'Restore failed.',
    fileUnreadable: 'The file could not be read.',
    restored: (trips: number, invoices: number, entries: number, chain: string) =>
      `Restored: ${trips} trips, ${invoices} invoices, ${entries} audit entries. Audit chain after the restore: ${chain}.`,
    chainUnchanged: 'intact',
    chainProblems: (count: number) => `${count} findings`,
  },

  settings: {
    title: 'Settings',
    description: 'These details appear on every invoice and in every export.',
    tabCompany: 'Company',
    tabInvoice: 'Invoice layout',
    tabApp: 'App & driver',
    tabVehicles: 'Vehicles',
    saved: 'Saved.',
    unsaved: 'There are unsaved changes.',

    companyCard: 'Company',
    companyName: 'Company name',
    ownerName: 'Owner / management',
    street: 'Street and number',
    postalCode: 'Postcode',
    city: 'Town',
    phone: 'Phone',
    email: 'Email',
    emailHint: 'The electronic address BT-34 of the e-invoice.',
    taxCard: 'Tax and licence',
    taxNumber: 'Tax number',
    taxNumberHint: 'One of the two is mandatory: § 14 Abs. 4 Nr. 2 UStG.',
    vatId: 'VAT ID',
    registrationNumber: 'Commercial register number',
    registrationHint: 'BT-30. Only needed for the e-invoice if there is no VAT ID.',
    licenceType: 'Licence type',
    licenceMietwagen: 'Private hire (§ 49 PBefG)',
    licenceTaxi: 'Taxi (§ 47 PBefG)',
    kleinunternehmer: 'Small business scheme',
    kleinunternehmerNo: 'No — VAT is shown',
    kleinunternehmerYes: 'Yes — § 19 UStG, no VAT',
    taxiNote:
      'As a taxi operator the reduced rate of 7 % applies to trips within one municipality or up to 50 km (§ 12 Abs. 2 Nr. 10 Buchst. b UStG). Beyond that the standard rate applies.',
    mietwagenNote:
      'Private hire under § 49 PBefG is not favoured: 19 % always applies, whatever the distance. The reduced rate is reserved for taxis under § 47 PBefG — repeatedly confirmed by the BFH and left standing by the BVerfG.',
    bankCard: 'Bank details',
    bankName: 'Bank',
    iban: 'IBAN',
    bic: 'BIC',

    invoiceCard: 'Invoice layout',
    logo: 'Logo',
    logoHint: 'PNG or JPEG, appears top right.',
    logoTypeError: 'PNG or JPEG only.',
    logoSizeError: 'The logo should stay under 500 kB — it goes into every invoice.',
    accentColor: 'Accent colour',
    paymentTerms: 'Payment terms',
    paymentTermsHint: 'Default text on every new invoice.',
    footerNote: 'Footer note',
    footerNoteHint: 'For instance a note on reverse charge or the commercial register.',

    driverCard: 'Driver and language',
    displayName: 'Driver name',
    driverCode: 'Driver ID',
    driverCodeHint: 'Mandatory field under BMF 11.03.2024.',
    language: 'Interface language',
    languageHint:
      'Invoices, the GoBD archive and the Verfahrensdokumentation stay German — they go to customers and to the tax office.',
    driverSaved: 'Driver details saved.',
    driverNameRequired: 'Please enter a name.',
    saveDriver: 'Save driver details',

    distanceCard: 'Distance measurement',
    distanceAuthority: 'Authoritative value',
    distanceComputed: 'Calculated route (Geoapify)',
    distanceOdometer: 'Odometer — entered by hand',
    kassenSichVTitle: 'Open legal question — please discuss with your tax adviser',
    kassenSichVBody:
      'Since 01.02.2026 § 1 Abs. 2 Satz 2 KassenSichV also covers app-based systems “insofar as they take over the function of an EU taximeter or distance recorder”. An app that determines the distance itself is plausibly covered — which would require a certified TSE and a conformity assessment under metrology law. There is no case law or administrative guidance yet. Switching to “odometer” moves the authoritative value without migrating any data.',
    geoCard: 'Address lookup',
    geoKey: 'Geoapify API key',
    geoKeyPlaceholder: 'without a key: learned addresses only',
    geoLat: 'Search bias latitude',
    geoLon: 'Longitude',
    geoCountry: 'Country',
    lockCard: 'Locking and reconciliation',
    lockDays: 'Lock after days',
    lockDaysHint: 'The safe harbour in BMF 03.03.2022 Rn. 29 is 7 days.',
    tolerance: 'Odometer tolerance (km)',
    toleranceHint: 'The shift warns above this deviation between trips and odometer difference.',
    backupCard: 'Backup',
    warnAfter: 'Warn after days',
    blockAfter: 'Block month-end close after days',
    lastBackupAt: (when: string) => `Last backup: ${when}.`,
    neverBackedUp: 'No backup has ever been made.',

    vehiclesCard: 'Vehicles',
    vehiclesNote:
      'The vehicle number is the “Mietwagenkennung” from the BMF circular of 11 March 2024 and appears in every export.',
    noVehicles: 'No vehicle added yet.',
    vehicleNumber: (code: string) => ` · no. ${code}`,
    deactivate: 'Retire',
    reactivate: 'Reactivate',
    addVehicle: 'Add a vehicle',
    licensePlate: 'Number plate',
    vehicleCode: 'Vehicle number',
    vehicleCodeHint: 'Leave empty for the next free number.',
    make: 'Make',
    model: 'Model',
    createVehicle: 'Add vehicle',
    creatingVehicle: 'Adding …',
    errPlate: 'The number plate is missing.',
    createFailed: 'Could not be created.',
  },

  audit: {
    loading: 'Loading the audit log …',
    empty: 'No entries.',
    reason: 'Reason:',
    actions: {
      create: 'Created',
      update: 'Changed',
      delete: 'Deleted',
      lock: 'Locked',
      unlock: 'Lock lifted',
      'invoice.issue': 'Invoice issued',
      'invoice.cancel': 'Cancelled',
      'invoice.pay': 'Payment recorded',
      'invoice.dun': 'Reminder created',
      'shift.close': 'Shift closed',
      export: 'Exported',
      backup: 'Backed up',
      restore: 'Restored',
      login: 'Signed in',
      'account.create': 'Account created',
    },
    fields: {
      date: 'Date',
      startedAt: 'Start of trip',
      endedAt: 'End of trip',
      passengerId: 'Passenger',
      fromAddressId: 'From',
      toAddressId: 'To',
      tripType: 'Trip type',
      distanceMeters: 'Distance',
      computedDistanceMeters: 'Calculated distance',
      distanceOverridden: 'Distance changed by hand',
      distanceSource: 'Source of the distance',
      fareGrossCents: 'Fare, gross',
      vatRatePercent: 'VAT rate',
      paymentMethod: 'Payment method',
      tipCents: 'Tip',
      comment: 'Comment',
      orderId: 'Order',
      invoiceId: 'Invoice',
      shiftId: 'Shift',
      vehicleId: 'Vehicle',
      driverId: 'Driver',
      lockedAt: 'Locked on',
      odometerStartMeters: 'Odometer at start',
      odometerEndMeters: 'Odometer at end',
      breaks: 'Breaks',
      signedOffBy: 'Driver sign-off',
      closedAt: 'Shift closed',
      sequenceNumber: 'Sequence number',
      receivedAt: 'Order received',
      channel: 'Order channel',
      ordererName: 'Orderer',
      ordererAddress: 'Orderer address',
      ordererPhone: 'Orderer phone',
      ordererEmail: 'Orderer email',
      pickupLocation: 'Pick-up point',
      destination: 'Destination',
      acceptedAt: 'Order accepted at',
      acceptancePlace: 'Place of acceptance',
      requestedPickupAt: 'Requested pick-up',
      invoiceNumber: 'Invoice number',
      status: 'Status',
      issueDate: 'Invoice date',
      dueDate: 'Due on',
      customerName: 'Customer',
      customerAddress: 'Customer address',
      netTotalCents: 'Net amount',
      vatTotalCents: 'VAT',
      grossTotalCents: 'Gross amount',
      paidAt: 'Paid on',
      dunningLevel: 'Reminder level',
      lastName: 'Surname',
      firstName: 'First name',
      company: 'Company',
      phone: 'Phone',
      email: 'Email',
      label: 'Label',
      street: 'Street',
      houseNumber: 'House number',
      postalCode: 'Postcode',
      city: 'Town',
      notes: 'Notes',
      displayName: 'Driver name',
      driverCode: 'Driver ID',
    },
    booleanTrue: 'yes',
    booleanFalse: 'no',
  },

  labels: {
    payment: {
      bar: 'Cash',
      ec: 'Debit card',
      kreditkarte: 'Credit card',
      rechnung: 'On account',
      app: 'App / broker',
      sonstiges: 'Other',
    },
    tripType: {
      tariffahrt: 'Tariff trip',
      sonstige: 'Other trip',
    },
    distanceSource: {
      geoapify: 'calculated automatically (Geoapify)',
      manual: 'entered by hand',
      odometer: 'odometer',
    },
    channel: {
      telefon: 'Phone',
      email: 'Email / in writing',
      funk: 'Radio',
      app: 'App',
      vermittler: 'Broker',
      persoenlich: 'In person',
      stammkunde: 'Regular customer',
      sonstiges: 'Other',
    },
    acceptance: {
      betriebssitz: 'Business premises',
      wohnung: 'Operator’s home',
      funk: 'During a trip (by phone)',
    },
    invoiceStatus: {
      entwurf: 'Draft',
      offen: 'Open',
      bezahlt: 'Paid',
      storniert: 'Cancelled',
      ueberfaellig: 'Overdue',
    },
    dunning: {
      0: 'No reminder',
      1: 'Payment reminder',
      2: 'First reminder',
      3: 'Second reminder',
    },
    eInvoice: {
      keine: 'No e-invoice',
      facturx: 'Factur-X / ZUGFeRD (EN 16931)',
      'xrechnung-ubl': 'XRechnung 3.0.2 (UBL)',
      'xrechnung-cii': 'XRechnung 3.0.2 (CII)',
    },
  },

  backupStatus: {
    never:
      'No backup has ever been made. Your records currently exist on this device only.',
    overdue: (days: number) =>
      `Last backup ${days} days ago. The month-end close is blocked until a current backup exists.`,
    due: (days: number) => `Last backup ${days} days ago. Please back up now.`,
    today: 'Backed up today.',
    ok: (days: number) => `Last backed up ${days} days ago.`,
    lastNever: 'never',
    lastToday: 'today',
    lastYesterday: 'yesterday',
    lastDaysAgo: (days: number) => `${days} days ago`,
  },
};
